import { RoleEnum, SpaceDto } from "@braoshzmvavz/picnube-common";
import {
  Button,
  LoadingOverlay,
  Popover,
  Select,
  Text,
  TextInput,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import React, { useEffect, useState } from "react";
import { HiArrowDown, HiExclamation, HiX } from "react-icons/hi";
import { HiMiniQuestionMarkCircle } from "react-icons/hi2";
import { MdSave } from "react-icons/md";
import { useAppSelector } from "src/app/hooks";
import useApi from "src/services/useApi";

interface SpaceUser {
  email: string;
  role: string;
  isNew?: boolean;
  isUpdated?: boolean;
}

const roles = ["Owner", "Admin", "Upload", "Read"];
const selectableRoles = ["Admin", "Upload", "Read", "None"];

const roleToApiValue = (role: string) => {
  if (role === "Owner") return RoleEnum.OWNER;
  if (role === "Admin") return RoleEnum.ADMIN;
  if (role === "Upload") return RoleEnum.WRITE;
  return RoleEnum.READ;
};

const apiValueToRole = (value: string) => {
  console.log(value);
  if (value === RoleEnum.OWNER) return "Owner";
  if (value === RoleEnum.ADMIN) return "Admin";
  if (value === RoleEnum.WRITE) return "Upload";
  return "Read";
};

export default function SpaceUsersModal(props: {
  space: SpaceDto;
  onSave: (addUsers: SpaceUser[], removeUsers: SpaceUser[]) => void;
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const [users, setUsers] = useState<SpaceUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const computedColorScheme = useComputedColorScheme("dark");

  const profile = useAppSelector((state) => state.profile);

  const { getSpaceAccessInfo, updateSpaceAccess } = useApi();
  const [helpOpened, { close, open }] = useDisclosure(false);

  useEffect(() => {
    setIsLoading(true);

    getSpaceAccessInfo(props.space.id).then((accessInfo) => {
      console.log(accessInfo);
      setUsers(
        accessInfo.users
          .sort(
            (a, b) =>
              roles.indexOf(apiValueToRole(a.role)) -
                roles.indexOf(apiValueToRole(b.role)) || // Sort by role first
              a.email.localeCompare(b.email) // Sort by email second
          )
          .map((user: any) => {
            return {
              email: user.email,
              role: apiValueToRole(user.role),
            };
          })
      );
      setIsLoading(false);
    });
  }, [props.space]);

  const form = useForm({
    initialValues: {
      email: "",
      role: "Read",
    },
    clearInputErrorOnChange: true,
    validate: {
      email: (val) =>
        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(val)
          ? null
          : "Invalid email",
    },
  });

  const addUser = () => {
    form.validate();
    if (!form.isValid()) return;

    users.push({
      email: form.values.email,
      role: form.values.role,
      isNew: true,
    });
    setUsers([...users]);
    form.reset();
  };

  const save = () => {
    const removeUsers: SpaceUser[] = [];
    const addUsers: SpaceUser[] = users
      .filter((u) => (u.isNew || u.isUpdated) && u.role !== "None")
      .map((u) => ({
        email: u.email,
        role: roleToApiValue(u.role),
      }));

    users.forEach((u) => {
      if (u.isUpdated && u.role === "None") {
        removeUsers.push({
          email: u.email,
          role: roleToApiValue(u.role),
        });
      }
    });

    console.log("addUsers", addUsers);
    console.log("removeUsers", removeUsers);

    props.onSave(addUsers, removeUsers);
  };

  const getRowClass = (row: SpaceUser) => {
    const styles =
      "flex flex-row justify-between items-center text-sm border-t border-grey-800 ";

    if (row.isNew) {
      return styles + "bg-blue-400 bg-opacity-10";
    }

    if (row.role === "None") {
      return styles + "bg-red-400 bg-opacity-10";
    }

    return styles;
  };

  const renderUsersRolesList = () => {
    return (
      <div className="flex flex-col max-w-full w-full overflow-auto">
        <div className="flex flex-col w-full overflow-auto">
          <form
            className="flex flex-col xs:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              addUser();
            }}
          >
            <div className="flex flex-1 flex-row justify-between items-start gap-3 mb-4 xs:mb-0 flex-wrap">
              <TextInput
                label="New user email"
                className="flex-1 w-full min-w-32"
                placeholder="example@email.com"
                value={form.values.email}
                onChange={(event) =>
                  form.setFieldValue("email", event.currentTarget.value)
                }
                radius="md"
                {...form.getInputProps("email")}
              />
              <Select
                label="Role"
                className="w-32 min-w-32"
                data={selectableRoles.filter((role) => role !== "None")}
                defaultValue="Read"
                allowDeselect={false}
                variant="filled"
                onChange={(value) => form.setFieldValue("role", value)}
                {...form.getInputProps("role")}
              />
            </div>

            <div
              className={
                "flex flex-row justify-center items-center gap-3 xs:ml-6 " +
                (!form.errors.email ? "xs:mt-5" : "")
              }
            >
              <Button
                type="submit"
                disabled={false}
                rightSection={<HiArrowDown />}
              >
                Invite user
              </Button>
            </div>
          </form>

          {users.length === 0 ? (
            <div className="flex flex-col gap-2 text-center mt-6 p-4 text-grey-600">
              <div className="text-xl">No users added yet</div>
              <div className="text-sm">
                Invite users to this space by adding their email addresses
              </div>
            </div>
          ) : (
            <div
              className="flex flex-row justify-between items-center mt-6
                text-sm font-bold border-b border-grey-800"
            >
              <div className="w-full p-2">User</div>
              <div className="p-1 w-32 min-w-32">Role</div>
            </div>
          )}

          {users.map((row) => (
            <div className={getRowClass(row)} key={row.email}>
              <div className="w-full p-2 overflow-hidden text-wrap overflow-ellipsis">
                {row.email}
              </div>

              <div className="p-1 w-32 min-w-32">
                {row.role === "Owner" ? (
                  <div>Owner</div>
                ) : (
                  <Select
                    data={selectableRoles}
                    defaultValue={row?.role ?? "Read"}
                    allowDeselect={false}
                    variant="unstyled"
                    onChange={(value) => {
                      row.role = value;
                      row.isUpdated = true;
                      setUsers([...users]);
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContentOrLoader = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-2 h-48 w-full">
          <LoadingOverlay
            visible={isLoading}
            zIndex={400}
            overlayProps={{
              blur: "xl",
              color: computedColorScheme === "dark" ? "black" : "black",
              opacity: computedColorScheme === "dark" ? "70%" : "30%",
            }}
            loaderProps={{ color: "blue", type: "oval", size: "md" }}
          />
        </div>
      );
    }

    return (
      <>
        {renderUsersRolesList()}

        <div className="flex flex-row justify-between w-full mt-4">
          <div className="flex flex-row justify-start items-center">
            <Popover
              width={300}
              position="top-start"
              withArrow
              shadow="md"
              opened={helpOpened}
            >
              <Popover.Target>
                <Text
                  onMouseEnter={open}
                  onMouseLeave={close}
                  c="dimmed"
                  size="sm"
                  className="flex flex-row items-center gap-2 hover:underline hover:cursor-pointer"
                  onClick={helpOpened ? close : open}
                >
                  <HiMiniQuestionMarkCircle />
                  What does each role mean?
                </Text>
              </Popover.Target>
              <Popover.Dropdown style={{ pointerEvents: "none" }}>
                <Text size="sm">
                  <Text fw={500}>Owner / Admin</Text> Can manage everything,
                  including delete content.
                </Text>
                <Text size="sm" mt={"md"}>
                  <Text fw={500}>Upload</Text> Can upload files and create
                  folders, but can not delete content.
                </Text>
                <Text size="sm" mt={"md"}>
                  <Text fw={500}>Read</Text> Can view files and folders, but can not upload or delete content.
                </Text>
              </Popover.Dropdown>
            </Popover>
          </div>
          <div className="flex flex-row justify-end items-center">
            <Button
              variant="filled"
              leftSection={<MdSave />}
              onClick={() => save()}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-4 w-full">{renderContentOrLoader()}</div>
  );
}
